/**
 * Add / edit shipping address (zoolyfe.id — alamat_pengiriman).
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  SafeAreaView,
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  Modal,
  Switch,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { goToProfileTab } from '../navigation/goToProfileTab';
import AppHeader from '../components/AppHeader';
import {
  createShippingAddress,
  updateShippingAddress,
  getShippingAddress,
  getShippingAddresses,
} from '../services/shippingAddressApi';
import MapComponent from '../components/maps/MapComponent';
import LocationAutoComplete from '../components/maps/LocationAutoComplete';
import AddressWilayahPicker from '../components/AddressWilayahPicker';
import Constants from "expo-constants";
import { APIProvider } from '@vis.gl/react-google-maps';
import { getDeviceCoordinates, isValidMapCoordinate } from '../utils/deviceGeolocation';
import { fetchGeomap, parseGeocodeLabel, resolveBiteshipArea, formatWilayahLabel } from '../utils/shippingPinpoint';

const MAX_DETAIL = 255;

export default function ShippingAddressScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const returnToReferral = !!route.params?.returnToReferral;
  const rawEditId = route.params?.editId;
  const editId =
    rawEditId != null && rawEditId !== '' && !Number.isNaN(Number(rawEditId))
      ? Number(rawEditId)
      : null;
  const isEdit = editId !== null && editId > 0;
  const returnParams = route.params?.returnParams || {};

  const [loadingAddr, setLoadingAddr] = useState(isEdit);
  const [namaAlamat, setNamaAlamat] = useState('');
  const [namaPenerima, setNamaPenerima] = useState('');
  const [noTelp, setNoTelp] = useState('');
  const [provinsi, setProvinsi] = useState('');
  const [kota, setKota] = useState('');
  const [kecamatan, setKecamatan] = useState('');
  const [kodepos, setKodepos] = useState('');
  const [detailAlamat, setDetailAlamat] = useState('');
  const [catatan, setCatatan] = useState('');
  const [kodeBiteship, setKodeBiteship] = useState('');
  const [setAsPrimary, setSetAsPrimary] = useState(true);
  /** Cached count before "add"; first-ever address forces primary in API & UI */
  const [priorAddressCount, setPriorAddressCount] = useState(null);
  const [saving, setSaving] = useState(false);

  // address pinpoint
  const [addressCoordinate, setAddressCoordinate] = useState(null);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [locating, setLocating] = useState(false);
  const [wilayahPickerOpen, setWilayahPickerOpen] = useState(false);
  const pinSeq = useRef(0);
  const wilayahLockedRef = useRef(false);

  // Custom Alert Modal State
  const [alertModal, setAlertModal] = useState({
    visible: false,
    title: '',
    message: '',
    type: 'error', // 'error' or 'success'
    onConfirm: null
  });

  // Custom Alert Functions
  const showCustomAlert = (title, message, type = 'error', onConfirm = null) => {
    setAlertModal({
      visible: true,
      title,
      message,
      type,
      onConfirm
    });
  };

  const hideCustomAlert = () => {
    setAlertModal(prev => ({ ...prev, visible: false }));
  };

  const handleAlertConfirm = () => {
    if (alertModal.onConfirm) {
      alertModal.onConfirm();
    }
    hideCustomAlert();
  };

  const applyCoordinate = useCallback((coord, options = {}) => {
    if (!coord) return;
    const fillWilayah = options.fillWilayah !== false;
    const seq = ++pinSeq.current;
    setAddressCoordinate({
      latitude: coord.latitude,
      longitude: coord.longitude,
    });
    void (async () => {
      try {
        const data = await fetchGeomap(coord.latitude, coord.longitude);
        if (seq !== pinSeq.current) return;
        setCurrentAddress(parseGeocodeLabel(data));
        if (!fillWilayah || wilayahLockedRef.current) return;
        const area = await resolveBiteshipArea(data);
        if (seq !== pinSeq.current || wilayahLockedRef.current) return;
        if (area) {
          setProvinsi(area.nama_provinsi || '');
          setKota(area.nama_kota || '');
          setKecamatan(area.nama_kecamatan || '');
          setKodepos(String(area.kodepos || ''));
          setKodeBiteship(area.kode || '');
        }
      } catch {
        if (seq !== pinSeq.current) return;
        setCurrentAddress('Location not found');
      }
    })();
  }, []);

  const onPinChange = useCallback((coord) => {
    wilayahLockedRef.current = false;
    applyCoordinate(coord, { fillWilayah: true });
  }, [applyCoordinate]);

  const getCurrentLocation = async () => {
    if (locating) return;
    setLocating(true);
    try {
      const coord = await getDeviceCoordinates();
      onPinChange(coord);
    } catch (e) {
      showCustomAlert('Perhatian', e.message || 'Tidak bisa mengambil lokasi saat ini.', 'error');
    } finally {
      setLocating(false);
    }
  };

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const a = await getShippingAddress(editId);
        if (cancelled || !a) return;
        setNamaAlamat(a.nama_alamat || '');
        setNamaPenerima(a.nama_penerima || '');
        setNoTelp(a.no_telp || '');
        setProvinsi(a.provinsi || '');
        setKota(a.kota || '');
        setKecamatan(a.kecamatan || '');
        setKodepos(a.kodepos || '');
        setDetailAlamat(a.detail_alamat || '');
        setCatatan(a.catatan || '');
        setKodeBiteship(a.kode_biteship || '');
        setSetAsPrimary(a.alamat_utama === 1);
        const hasWilayah = !!(
          String(a.kode_biteship || '').trim()
          || (String(a.provinsi || '').trim()
            && String(a.kota || '').trim()
            && String(a.kecamatan || '').trim()
            && String(a.kodepos || '').trim())
        );
        wilayahLockedRef.current = hasWilayah;

        const lat = Number(a.latitude);
        const lng = Number(a.longitude);

        if (isValidMapCoordinate(lat, lng)) {
          applyCoordinate({ latitude: lat, longitude: lng }, { fillWilayah: !hasWilayah });
        } else {
          setAddressCoordinate(null);
          setCurrentAddress(null);
        }
      } catch (e) {
        Alert.alert('Gagal', e.message || 'Tidak dapat memuat alamat', [
          { text: 'OK', onPress: () => navigation.goBack() },
        ]);
      } finally {
        if (!cancelled) setLoadingAddr(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, editId, navigation, applyCoordinate]);

  useEffect(() => {
    if (isEdit) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const rows = await getShippingAddresses();
        if (cancelled) return;
        setPriorAddressCount(rows.length);
        if (rows.length === 0) {
          setSetAsPrimary(true);
        }
      } catch {
        if (!cancelled) setPriorAddressCount(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit]);

  const onSubmit = useCallback(async () => {
    const d = detailAlamat.trim();
    if (!addressCoordinate) {
      showCustomAlert(
        `Perhatian`,
        `Belum pinpoint lokasi alamatmu.`,
        `error`
      );
      return;
    }
    if (!namaAlamat.trim() || !namaPenerima.trim() || !d) {
      showCustomAlert(
        `Perhatian`,
        `Isi nama alamat, nama penerima, dan detail alamat.`,
        `error`
      );
      return;
    }
    if (d.length > MAX_DETAIL) {
      showCustomAlert(
        `Perhatian`,
        `Detail alamat maksimal ${MAX_DETAIL} karakter.`,
        `error`
      );
      return;
    }
    if (!noTelp.trim()) {
      showCustomAlert(
        `Perhatian`,
        `Nomor telepon wajib diisi.`,
        `error`
      );
      return;
    }
    if (!provinsi.trim() || !kota.trim() || !kecamatan.trim() || !kodepos.trim()) {
      showCustomAlert(
        `Perhatian`,
        `Pilih provinsi, kota/kab, dan kode pos.`,
        `error`
      );
      return;
    }

    let countForSubmit = priorAddressCount;
    if (!isEdit && countForSubmit == null) {
      try {
        countForSubmit = (await getShippingAddresses()).length;
      } catch {
        countForSubmit = null;
      }
    }
    const firstAddressForcedPrimary =
      !isEdit && countForSubmit !== null && Number(countForSubmit) === 0;

    const payload = {
      nama_alamat: namaAlamat.trim(),
      nama_penerima: namaPenerima.trim(),
      no_telp: noTelp.trim(),
      detail_alamat: d,
      provinsi: provinsi.trim(),
      kota: kota.trim(),
      kecamatan: kecamatan.trim(),
      kodepos: kodepos.trim(),
      catatan: catatan.trim(),
      kode_biteship: kodeBiteship.trim(),
      latitude: String(addressCoordinate?.latitude ?? '0'),
      longitude: String(addressCoordinate?.longitude ?? '0'),
      set_as_primary: firstAddressForcedPrimary ? true : setAsPrimary,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await updateShippingAddress(editId, payload);
      } else {
        await createShippingAddress(payload);
      }
      navigation.navigate('ShippingAddressesList', returnParams);
      // goToProfileTab(navigation);
      const msg = returnToReferral
        ? 'Alamat tersimpan. Buka lagi Kode Referral dari Profil jika ingin klaim hadiah.'
        : 'Alamat tersimpan.';
      setTimeout(() => {
        Alert.alert('Berhasil', msg);
      }, 350);
    } catch (e) {
      Alert.alert('Gagal menyimpan', e.message || 'Coba lagi.');
    } finally {
      setSaving(false);
    }
  }, [
    namaAlamat,
    namaPenerima,
    noTelp,
    provinsi,
    kota,
    kecamatan,
    kodepos,
    detailAlamat,
    catatan,
    kodeBiteship,
    setAsPrimary,
    priorAddressCount,
    returnToReferral,
    navigation,
    isEdit,
    editId,
    addressCoordinate
  ]);

  if (loadingAddr) {
    return (
      <SafeAreaView style={styles.safe}>
        <AppHeader title="Alamat" onBack={() => navigation.goBack()} />
        <View style={styles.centered}>
          <ActivityIndicator size="large" color="#64748B" />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe}>
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 8 : 0}
      >
        <AppHeader title={isEdit ? 'Ubah alamat' : 'Tambah alamat'} onBack={() => navigation.goBack()} />

        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={styles.hint}>
            Pinpoint lokasi di peta atau cari alamat. Provinsi, kota/kab, dan kode pos bisa terisi dari pin, dan tetap bisa dipilih manual.
          </Text>


          <Text style={{
            fontWeight: '600',
            fontSize: 13,
            color: "rgb(71, 85, 105)",
            marginBottom: 8,
          }}>
            Pinpoint Lokasi Alamatmu</Text>
          <APIProvider apiKey={Constants.expoConfig?.extra?.GOOGLE_MAPS_API_KEY}>
            <LocationAutoComplete
              placeholder="Cari alamatmu"
              onSelectLocation={onPinChange}
            />
            <MapComponent 
              style={{
                width: "100%",
                height: 200,
                marginBottom: 12,
                borderRadius: 8,
                overflow: "clip",
              }}
              selectedLocation={addressCoordinate} 
              onSelectLocation={onPinChange} 
            />

            {currentAddress && (
              <Text
                style={{
                  borderWidth: 1,
                  borderColor: "#1E293B",
                  borderRadius: 6,
                  backgroundColor: "#FFFFFF",
                  fontSize: 14,
                  padding: 8,
                  marginBottom: 12,
                }}
                >{currentAddress}</Text>
            )}
            <TouchableOpacity 
              style={{
                padding: 8,
                display: "flex",
                flexDirection: "row",
                justifyContent: 'center',
                alignItems: 'center',
                gap: 4,
                borderRadius: 6,
                backgroundColor: "#1E293B",
                marginHorizontal: "auto",
                marginBottom: 16,
                opacity: locating ? 0.7 : 1,
              }} 
              onPress={() => {
                getCurrentLocation();
              }}
              disabled={locating}
              activeOpacity={0.85}
            >
              {locating ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Ionicons name="location-outline" size={16} color="#FFFFFF" />
              )}
              <Text style={{
                fontWeight: '500',
                fontSize: 14,
                lineHeight: 16,
                color: "#FFFFFF",
              }}>{locating ? 'Mengambil lokasi…' : 'Gunakan Lokasi Saat Ini'}</Text>
            </TouchableOpacity>
          </APIProvider>

          <Field label="Nama alamat (label)" value={namaAlamat} onChangeText={setNamaAlamat} placeholder="Contoh: Rumah" />
          <Field label="Nama penerima" value={namaPenerima} onChangeText={setNamaPenerima} placeholder="Nama lengkap" />
          <Field
            label="No. telepon / WhatsApp"
            value={noTelp}
            onChangeText={setNoTelp}
            placeholder="08xxxxxxxxxx"
            keyboardType="phone-pad"
          />

          <Text style={styles.label}>Alamat *</Text>
          <TouchableOpacity
            style={styles.alamatPick}
            onPress={() => setWilayahPickerOpen(true)}
            activeOpacity={0.85}
          >
            <Text style={[styles.alamatPickText, !kecamatan && styles.alamatPickMuted]}>
              {formatWilayahLabel(provinsi, kota, kecamatan, kodepos)
                || 'Pilih provinsi, kota/kab, dan kode pos'}
            </Text>
          </TouchableOpacity>

          <Text style={styles.label}>Detail alamat (jalan, RT/RW, gedung)</Text>
          <TextInput
            style={[styles.input, styles.textArea]}
            value={detailAlamat}
            onChangeText={setDetailAlamat}
            placeholder="Maks. 255 karakter"
            placeholderTextColor="#94A3B8"
            multiline
            maxLength={MAX_DETAIL}
            textAlignVertical="top"
          />
          <Text style={styles.counter}>
            {detailAlamat.length}/{MAX_DETAIL}
          </Text>

          <Field label="Catatan kurir (opsional)" value={catatan} onChangeText={setCatatan} placeholder="Patokan, dll." />

          {isEdit ? (
            <View style={styles.switchRow}>
              <Text style={styles.switchLabel}>Jadikan alamat utama</Text>
              <Switch
                value={setAsPrimary}
                onValueChange={setSetAsPrimary}
                trackColor={{ false: '#CBD5E1', true: '#86EFAC' }}
                thumbColor={setAsPrimary ? '#166534' : '#f4f3f4'}
              />
            </View>
          ) : priorAddressCount === null ? (
            <Text style={styles.smallWait}>Memuat pengaturan alamat…</Text>
          ) : priorAddressCount === 0 ? (
            <Text style={styles.firstAddressHint}>
              Ini alamat pertamamu — otomatis diset sebagai alamat utama untuk pengiriman dan klaim hadiah.
            </Text>
          ) : (
            <View style={styles.switchRow}>
              <Text style={styles.switchLabel}>Jadikan alamat utama</Text>
              <Switch
                value={setAsPrimary}
                onValueChange={setSetAsPrimary}
                trackColor={{ false: '#CBD5E1', true: '#86EFAC' }}
                thumbColor={setAsPrimary ? '#166534' : '#f4f3f4'}
              />
            </View>
          )}

          <TouchableOpacity
            style={[styles.submitBtn, saving && styles.submitDisabled]}
            onPress={onSubmit}
            disabled={saving}
            activeOpacity={0.85}
          >
            {saving ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.submitText}>{isEdit ? 'Simpan perubahan' : 'Simpan alamat'}</Text>
            )}
          </TouchableOpacity>
        </ScrollView>
      </KeyboardAvoidingView>

      <AddressWilayahPicker
        visible={wilayahPickerOpen}
        onClose={() => setWilayahPickerOpen(false)}
        onSelect={(area) => {
          wilayahLockedRef.current = true;
          setProvinsi(area.nama_provinsi || '');
          setKota(area.nama_kota || '');
          setKecamatan(area.nama_kecamatan || '');
          setKodepos(String(area.kodepos || ''));
          setKodeBiteship(area.kode || '');
        }}
      />

      <Modal
        animationType="fade"
        transparent={true}
        visible={alertModal.visible}
        onRequestClose={hideCustomAlert}
      >
        <View style={styles.customModalOverlay}>
          <View style={styles.customModalContainer}>
            <View style={[styles.customModalHeader, alertModal.type === 'success' ? styles.successHeader : styles.errorHeader]}>
              <Text style={styles.customModalTitle}>{alertModal.title}</Text>
            </View>
            <View style={styles.customModalBody}>
              <Text style={styles.customModalMessage}>{alertModal.message}</Text>
            </View>
            <View style={styles.customModalFooter}>
              {alertModal.onConfirm ? (
                <View style={styles.customModalButtonsRow}>
                  <TouchableOpacity 
                    style={[styles.customModalButton, styles.cancelButton]}
                    onPress={hideCustomAlert}
                  >
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    style={[styles.customModalButton, alertModal.type === 'success' ? styles.successButton : styles.errorButton]}
                    onPress={handleAlertConfirm}
                  >
                    <Text style={styles.customModalButtonText}>{alertModal.type === 'success' ? 'OK' : 'Confirm'}</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <TouchableOpacity 
                  style={[styles.customModalButton, alertModal.type === 'success' ? styles.successButton : styles.errorButton]}
                  onPress={hideCustomAlert}
                >
                  <Text style={styles.customModalButtonText}>OK</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

function Field({ label, value, onChangeText, placeholder, keyboardType, editable = true }) {
  return (
    <View style={styles.field}>
      <Text style={styles.label}>{label}</Text>
      <TextInput
        style={[styles.input, !editable && styles.inputReadonly]}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor="#94A3B8"
        keyboardType={keyboardType || 'default'}
        editable={editable}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#F8FAFC' },
  flex: { flex: 1 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  scroll: { flex: 1 },
  scrollContent: { padding: 16, paddingBottom: 40 },
  hint: {
    fontSize: 13,
    color: '#64748B',
    lineHeight: 20,
    marginBottom: 12,
    backgroundColor: '#EEF2FF',
    padding: 12,
    borderRadius: 10,
  },
  alamatPick: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 14,
  },
  alamatPickText: { fontSize: 15, color: '#0F172A' },
  alamatPickMuted: { color: '#94A3B8' },
  field: { marginBottom: 14 },
  label: { fontSize: 13, fontWeight: '600', color: '#475569', marginBottom: 6 },
  input: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    color: '#0F172A',
  },
  inputReadonly: {
    backgroundColor: '#F1F5F9',
    color: '#334155',
  },
  textArea: { minHeight: 100, paddingTop: 12 },
  counter: { alignSelf: 'flex-end', fontSize: 12, color: '#94A3B8', marginTop: 4, marginBottom: 14 },
  firstAddressHint: {
    marginBottom: 16,
    fontSize: 14,
    lineHeight: 20,
    color: '#475569',
    padding: 14,
    backgroundColor: '#ECFDF5',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#BBF7D0',
  },
  smallWait: {
    marginBottom: 16,
    fontSize: 14,
    color: '#64748B',
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
    backgroundColor: '#fff',
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  switchLabel: { fontSize: 15, fontWeight: '600', color: '#334155', flex: 1, marginRight: 12 },
  submitBtn: {
    backgroundColor: '#1E293B',
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
    marginTop: 8,
  },
  submitDisabled: { opacity: 0.7 },
  submitText: { color: '#fff', fontWeight: '700', fontSize: 16 },
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 24,
  },
  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
  },
  modalPopup: {
    backgroundColor: '#fff',
    borderRadius: 16,
    overflow: 'hidden',
    zIndex: 1,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18,
    shadowRadius: 16,
    elevation: 12,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  modalTitle: { fontSize: 17, fontWeight: '700', color: '#0F172A', flex: 1, marginRight: 8 },
  modalInput: {
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 4,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
  },
  modalSpinner: { marginVertical: 20 },
  modalList: {},
  modalListContent: { flexGrow: 1, paddingBottom: 12 },
  modalEmpty: { textAlign: 'center', color: '#94A3B8', padding: 24 },
  modalRow: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  modalRowText: { fontSize: 14, color: '#0F172A', fontWeight: '500' },
  modalRowSub: { fontSize: 12, color: '#64748B', marginTop: 4 },

  customModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  customModalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    overflow: 'hidden',
    width: '100%',
    maxWidth: 320,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 8,
  },
  customModalHeader: {
    padding: 20,
    paddingBottom: 15,
  },
  successHeader: {
    backgroundColor: '#10B981',
  },
  errorHeader: {
    backgroundColor: '#EF4444',
  },
  customModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  customModalBody: {
    padding: 20,
    paddingTop: 10,
    paddingBottom: 15,
  },
  customModalMessage: {
    fontSize: 16,
    color: '#374151',
    textAlign: 'center',
    lineHeight: 24,
  },
  customModalFooter: {
    padding: 20,
    paddingTop: 10,
  },
  customModalButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
  },
  customModalButtonsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  successButton: {
    backgroundColor: '#10B981',
    flex: 1,
  },
  errorButton: {
    backgroundColor: '#EF4444',
    flex: 1,
  },
  cancelButton: {
    backgroundColor: '#F3F4F6',
    flex: 1,
  },
  customModalButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  cancelButtonText: {
    color: '#6B7280',
    fontSize: 16,
    fontWeight: '600',
  },
});
